import { useEffect, useState } from "react";
import { toast } from "sonner";
import { getLocations, aiImportExcel, aiImportCommit } from "../lib/api";

export const ImportExcelModal = ({ open, onClose, onChanged }) => {
  const [locations, setLocations] = useState([]);
  const [locationId, setLocationId] = useState("");
  const [file, setFile] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState(null);

  useEffect(() => {
    if (!open) return;
    (async () => {
      const locs = await getLocations();
      setLocations(locs);
      setLocationId((p) => p || locs[0]?.id || "");
      setFile(null); setPreview(null);
    })();
  }, [open]);

  if (!open) return null;

  const analyze = async () => {
    if (!file) return toast.error("Pilih file Excel dulu");
    setAnalyzing(true); setPreview(null);
    try {
      const r = await aiImportExcel(file);
      if (!r.total) return toast.error("Tidak ada data terbaca dari Excel");
      setPreview(r);
      toast.success(`AI membaca ${r.total} item dari ${r.jobs.length} gedung`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal menganalisa Excel");
    } finally {
      setAnalyzing(false);
    }
  };

  const commit = async () => {
    if (!locationId) return toast.error("Pilih lokasi tujuan");
    if (!preview) return;
    setSaving(true);
    try {
      const r = await aiImportCommit({ location_id: locationId, jobs: preview.jobs });
      toast.success(`${r.created_items} item di ${r.created_jobs} gedung berhasil disimpan`);
      onChanged && onChanged();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal menyimpan data");
    } finally {
      setSaving(false);
    }
  };

  const inputCls = "w-full px-3 py-2 border border-slate-300 rounded-lg text-xs focus:ring-2 focus:ring-teal-500 outline-none bg-white font-semibold";

  return (
    <div className="no-print fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl my-auto flex flex-col max-h-[92vh] animate-fade-up" data-testid="import-excel-modal">
        <div className="px-6 py-4 bg-gradient-to-r from-emerald-600 to-teal-600 text-white flex justify-between items-center">
          <h3 className="text-base sm:text-lg font-bold flex items-center gap-2"><i className="ph-bold ph-microsoft-excel-logo text-emerald-100" /> Import Excel dengan AI</h3>
          <button onClick={onClose} className="text-emerald-100 hover:text-white p-1 rounded-lg hover:bg-white/10" data-testid="import-excel-close"><i className="ph-bold ph-x text-xl" /></button>
        </div>

        <div className="p-6 overflow-y-auto space-y-4 flex-1">
          <p className="text-xs text-slate-500">Unggah file Excel (.xlsx). AI akan mengenali kolom (gedung, lantai, ruang, kondisi, status, dll) secara otomatis, lalu Anda tinjau sebelum disimpan.</p>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-[11px] font-semibold text-slate-600 mb-1">Lokasi Tujuan</label>
              <select value={locationId} onChange={(e) => setLocationId(e.target.value)} className={inputCls} data-testid="import-location-select">
                {locations.length === 0 && <option value="">(Belum ada lokasi)</option>}
                {locations.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-[11px] font-semibold text-slate-600 mb-1">File Excel (.xlsx)</label>
              <input type="file" accept=".xlsx,.xls" onChange={(e) => { setFile(e.target.files[0]); setPreview(null); }} className="w-full text-xs file:mr-2 file:py-1.5 file:px-3 file:rounded-lg file:border-0 file:bg-emerald-50 file:text-emerald-700 file:font-bold file:text-xs" data-testid="import-file-input" />
            </div>
          </div>

          <button onClick={analyze} disabled={analyzing || !file} className="inline-flex items-center gap-2 bg-gradient-to-r from-indigo-500 to-violet-500 text-white px-4 py-2 rounded-lg text-xs font-bold shadow-sm hover:opacity-90 disabled:opacity-50" data-testid="import-analyze-btn">
            <i className={`ph-bold ${analyzing ? "ph-spinner animate-spin" : "ph-sparkle"}`} /> {analyzing ? "AI menganalisa..." : "Analisa dengan AI"}
          </button>

          {preview && (
            <div className="border border-slate-200 rounded-xl overflow-hidden" data-testid="import-preview">
              <div className="bg-slate-50 px-4 py-2 flex items-center justify-between">
                <span className="text-xs font-bold text-slate-700">Pratinjau: {preview.total} item • {preview.jobs.length} gedung</span>
                <span className="text-[10px] text-slate-400">Kolom terdeteksi oleh AI</span>
              </div>
              <div className="max-h-[40vh] overflow-y-auto divide-y divide-slate-100">
                {preview.jobs.map((j, i) => (
                  <div key={i} className="px-4 py-2.5" data-testid={`import-group-${i}`}>
                    <div className="flex items-center justify-between">
                      <p className="text-sm font-bold text-slate-800"><i className="ph-bold ph-buildings text-teal-600" /> {j.title}</p>
                      <span className="text-[11px] font-semibold text-slate-400">{j.count} item</span>
                    </div>
                    <p className="text-[11px] text-slate-500 mt-0.5 truncate">
                      Contoh: {[j.items[0]?.ruang, j.items[0]?.spesifikasi, j.items[0]?.kondisi, j.items[0]?.status].filter(Boolean).join(" • ")}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="px-6 py-4 border-t border-slate-200 flex justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 border border-slate-300 text-slate-700 text-xs font-bold rounded-lg hover:bg-slate-50">Batal</button>
          <button onClick={commit} disabled={!preview || saving} className="px-5 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold rounded-lg shadow-sm disabled:opacity-50" data-testid="import-commit-btn">
            {saving ? "Menyimpan..." : preview ? `Simpan ${preview.total} item` : "Simpan ke Database"}
          </button>
        </div>
      </div>
    </div>
  );
};
